import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Plus, Edit2, Trash2, ExternalLink, Search } from 'lucide-react';
import api from '../services/api';
import BrandIcon from '../components/BrandIcon';

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'HU', name: 'Hungary' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'HR', name: 'Croatia' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'RO', name: 'Romania' },
  { code: 'GR', name: 'Greece' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LV', name: 'Latvia' },
  { code: 'EE', name: 'Estonia' },
  { code: 'MT', name: 'Malta' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'SG', name: 'Singapore' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'EG', name: 'Egypt' },
  { code: 'IL', name: 'Israel' },
  { code: 'AE', name: 'UAE' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'RU', name: 'Russia' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'BY', name: 'Belarus' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'NZ', name: 'New Zealand' }
];

const Websites = () => {
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState(null);
  const [formData, setFormData] = useState({
    domain: '',
    brandName: '',
    brandIcon: null,
    brandNames: '',
    competitorBrands: '',
    ownedDomains: '',
    competitorDomains: '',
    country: 'US',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [deletingWebsite, setDeletingWebsite] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchWebsites();
  }, []);

  const fetchWebsites = async () => {
    try {
      const response = await api.get('/websites');
      setWebsites(response.data.websites);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch websites');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWebsite = () => {
    setEditingWebsite(null);
    setFormData({
      domain: '',
      brandName: '',
    brandIcon: null,
      brandNames: '',
      competitorBrands: '',
      ownedDomains: '',
      competitorDomains: '',
      country: 'US',
      description: '',
    });
    setDialogOpen(true);
  };

  const handleEditWebsite = (website) => {
    setEditingWebsite(website);
    setFormData({
      domain: website.domain,
      brandName: website.brandName,
      brandIcon: website.brandIcon||null,
      brandNames: Array.isArray(website.brandNames) ? website.brandNames.join(', ') : '',
      competitorBrands: Array.isArray(website.competitorBrands) ? website.competitorBrands.join(', ') : '',
      ownedDomains: Array.isArray(website.ownedDomains) ? website.ownedDomains.join(', ') : '',
      competitorDomains: Array.isArray(website.competitorDomains) ? website.competitorDomains.join(', ') : '',
      country: website.country || 'US',
      description: website.description || '',
    });
    setDialogOpen(true);
  };

  const cleanDomain = (domain) => {
    return domain
      .replace(/^https?:\/\//, '') // Remove http/https
      .replace(/^www\./, '')       // Remove www.
      .replace(/\/$/, '')          // Remove trailing slash
      .toLowerCase()
      .trim();
  };

  const cleanDomainList = (domainList) => {
    return domainList ? domainList.split(',').map(d => cleanDomain(d)).filter(d => d) : [];
  };

  const cleanBrandNameList = (brandList) => {
    return brandList ? brandList.split(',').map(b => b.trim()).filter(b => b) : [];
  };

  const handleIconUpload=async(event)=>{
    const file=event.target.files?.[0];if(!file)return;
    setError('');
    if(!['image/png','image/jpeg','image/webp'].includes(file.type)||file.size>2*1024*1024){setError('Choose a PNG, JPG or WebP image smaller than 2 MB.');event.target.value='';return;}
    try{
      const bitmap=await createImageBitmap(file);
      if(bitmap.width>4096||bitmap.height>4096){bitmap.close();throw new Error('Choose an image no larger than 4096 pixels on either side.');}
      const canvas=document.createElement('canvas');canvas.width=128;canvas.height=128;
      const ratio=Math.min(128/bitmap.width,128/bitmap.height),width=bitmap.width*ratio,height=bitmap.height*ratio;
      canvas.getContext('2d').drawImage(bitmap,(128-width)/2,(128-height)/2,width,height);bitmap.close();
      const brandIcon=canvas.toDataURL('image/png');
      setFormData(current=>({...current,brandIcon}));
    }catch(e){setError(e.message||'Unable to read that image. Try another PNG, JPG or WebP.');}
    event.target.value='';
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        ...formData,
        domain: cleanDomain(formData.domain),
        brandNames: cleanBrandNameList(formData.brandNames),
        competitorBrands: cleanBrandNameList(formData.competitorBrands),
        ownedDomains: cleanDomainList(formData.ownedDomains),
        competitorDomains: cleanDomainList(formData.competitorDomains),
      };


      if (editingWebsite) {
        await api.put(`/websites/${editingWebsite.id}`, payload);
      } else {
        const {data}=await api.post('/websites', payload);
        setDialogOpen(false);
        navigate(`/keywords/${data.website.id}`,{state:{promptSetup:{requestId:crypto.randomUUID()}}});
        return;
      }

      setDialogOpen(false);
      fetchWebsites();
    } catch (err) {
      console.error('Website submission error:', err.response?.data);
      const errorMessage = err.response?.data?.details
        ? `Validation error: ${err.response.data.details.map(d => d.message).join(', ')}`
        : err.response?.data?.message || 'Failed to save website';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteWebsite = async (websiteId) => {
    if (!window.confirm('Are you sure you want to delete this website? This will also delete all associated keywords and data.')) {
      return;
    }

    setDeletingWebsite(websiteId);
    setError('');
    try {
      await api.delete(`/websites/${websiteId}`);
      setWebsites(current => current.filter(website => website.id !== websiteId));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete website');
    } finally {
      setDeletingWebsite(null);
    }
  };

  const handleViewKeywords = (websiteId) => {
    navigate(`/keywords/${websiteId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Websites</h1>
        <Button onClick={handleAddWebsite}>
          <Plus className="mr-2 h-4 w-4" />
          Add Website
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {websites.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <h2 className="text-xl font-semibold text-muted-foreground mb-2">
              No websites added yet
            </h2>
            <p className="text-muted-foreground mb-4">
              Add your first website to start tracking citations
            </p>
            <Button onClick={handleAddWebsite}>
              <Plus className="mr-2 h-4 w-4" />
              Add Website
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {websites.map((website) => (
            <Card key={website.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-3"><BrandIcon website={website}/>{website.brandName}</CardTitle>
                <CardDescription>{website.domain}</CardDescription>

                <div className="flex items-center mt-2">
                  <Badge variant="secondary" className="mr-2">
                    {COUNTRIES.find(c => c.code === website.country)?.name || website.country} ({website.country})
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">

                {website.description && (
                  <p className="text-sm text-muted-foreground mb-4">
                    {website.description}
                  </p>
                )}

                {website.ownedDomains && website.ownedDomains.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground mb-1">
                      Owned Domains:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {website.ownedDomains.slice(0, 3).map((domain, index) => (
                        <Badge key={index} variant="default" className="text-xs">
                          {domain}
                        </Badge>
                      ))}
                      {website.ownedDomains.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{website.ownedDomains.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {website.competitorDomains && website.competitorDomains.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground mb-1">
                      Competitor Domains:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {website.competitorDomains.slice(0, 3).map((domain, index) => (
                        <Badge key={index} variant="warning" className="text-xs">
                          {domain}
                        </Badge>
                      ))}
                      {website.competitorDomains.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{website.competitorDomains.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewKeywords(website.id)}
                    className="flex-1"
                  >
                    <Search className="mr-2 h-4 w-4" />
                    Keywords
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(`https://${website.domain}`, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditWebsite(website)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteWebsite(website.id)}
                    aria-label={`Delete ${website.domain}`}
                    disabled={deletingWebsite !== null}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Website Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingWebsite ? 'Edit Website' : 'Add Website'}
            </DialogTitle>
            <DialogDescription>
              {editingWebsite?'Update brand details and citation classification.':'Add your brand and website. We will research up to five public company pages, then help you review and generate questions for weekly tracking. Research uses a small AI request within your shared budget.'}
            </DialogDescription>
          </DialogHeader>
          {error&&<Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          <div className="flex gap-4 items-center">
            <BrandIcon website={{...formData,domain:cleanDomain(formData.domain)}} className="w-14 h-14"/>
            <div><label htmlFor="brand-icon-upload" className="text-sm font-medium">Brand icon</label>
              <Input id="brand-icon-upload" type="file" accept="image/png,image/jpeg,image/webp" onChange={handleIconUpload} disabled={submitting}/>
              <p className="text-xs text-muted-foreground mt-1">Uses the website favicon by default. Upload a PNG, JPG or WebP up to 2 MB to replace it.</p>
              {formData.brandIcon&&<Button variant="ghost" size="sm" onClick={()=>setFormData({...formData,brandIcon:null})}>Use website favicon</Button>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium">Primary Domain</label>
              <Input
                autoFocus
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                placeholder="example.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter domain without protocol (http/https) - we'll clean it automatically
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Brand Name</label>
              <Input
                value={formData.brandName}
                onChange={(e) => setFormData({ ...formData, brandName: e.target.value })}
                placeholder="Your Brand"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Target Country</label>
              <Select value={formData.country} onValueChange={(value) => setFormData({ ...formData, country: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name} ({country.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Brand Names (comma-separated)</label>
              <Input
                value={formData.brandNames}
                onChange={(e) => setFormData({ ...formData, brandNames: e.target.value })}
                placeholder="YourBrand, Your Brand, YB"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Alternative brand names to detect in citation content. Leave empty to use only the main brand name.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Competitor Brand Names (comma-separated)</label>
              <Input
                value={formData.competitorBrands}
                onChange={(e) => setFormData({ ...formData, competitorBrands: e.target.value })}
                placeholder="Competitor One, Competitor Two"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Competitor brand names to detect in citation content
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Owned Domains (comma-separated)</label>
              <Input
                value={formData.ownedDomains}
                onChange={(e) => setFormData({ ...formData, ownedDomains: e.target.value })}
                placeholder="example.com, blog.example.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Domains that should be classified as 'brand' citations
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Competitor Domains (comma-separated)</label>
              <Input
                value={formData.competitorDomains}
                onChange={(e) => setFormData({ ...formData, competitorDomains: e.target.value })}
                placeholder="competitor1.com, competitor2.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Domains that should be classified as 'competitor' citations
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium">Description (optional)</label>
              <Textarea
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of your website..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formData.domain || !formData.brandName}
            >
              {submitting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : null}
              {editingWebsite ? 'Update' : 'Add & research website'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Websites;
